import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Center,
  Heading,
  HStack,
  Image,
  Pressable,
  Spinner,
  Stack,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { FiEdit, FiMap } from "react-icons/fi";
import { Marker, Polyline } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import ImageGallery from "react-image-gallery";
import styles from "./styles.module.css";
import colors from "../../../../constants/colors";
import { MdOutlineEmail } from "react-icons/md";
import routes from "../../../../routes";
import seat from "../../../../assets/icons/seat.svg";
import Itinerary from "../../../../components/itinerary";
import addCircle from "../../../../assets/icons/add-circle.svg";
import removeCircle from "../../../../assets/icons/remove-circle.svg";
import { StateContext, StatusContext } from "../../../../contexts";
import API, { getFileUrl } from "../../../../api";
import Cell from "../../../../components/cell";
import {
  formatCouponValue,
  formatToCurrency,
  getOriginalPrice,
} from "../../../../utils";
import Modal from "../../../../components/modal";
import Map from "../../../../components/map";
import mapIcons from "../../../../constants/mapIcons";
import QuotationFinishedModal from "../../../../components/quotationFinishedModal";
import ContactModal from "../../../../components/contactModal";
import Holder from "../../../../components/holder";
import UnitsModal from "../../../../components/unitsModal";

function AgencyExperienceCreationTransportSelectionPage() {
  const params = useParams();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [map, setMap] = useState();
  const [returnMap, setReturnMap] = useState();
  const [results, setResults] = useState([]);
  const [itinerary, setItinerary] = useState([]);
  const [numberOfPassengers, setNumberOfPassengers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingItinerary, setIsLoadingItinerary] = useState(false);
  const {
    isOpen: isMapModalOpen,
    onOpen: onMapModalOpen,
    onClose: onMapModalClose,
  } = useDisclose();
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [polyline, setPolyline] = useState("");
  const [returnPolyline, setReturnPolyline] = useState("");
  const [finished, setFinished] = useState(false);
  const {
    isOpen: isContactModalOpen,
    onOpen: onContactModalOpen,
    onClose: onContactModalClose,
  } = useDisclose();
  const [modalImages, setModalImages] = useState([]);
  const [modalAmenities, setModalAmenities] = useState([]);
  const [holder, setHolder] = useState(null);
  const [maximumUnits, setMaximumUnits] = useState(2);
  const {
    isOpen: isUnitsModalOpen,
    onClose: onCloseUnitsModal,
    onOpen: onOpenUnitsModal,
  } = useDisclose();
  const [isSaving, setIsSaving] = useState(false);

  const getQuotation = useCallback(async () => {
    try {
      if (!params.id || !state.jwt) {
        return;
      }
      setIsLoading(true);
      setIsLoadingItinerary(true);
      const {
        data: {
          data: {
            itinerary: _itinerary,
            numberOfPassengers: _numberOfPassengers,
            polyline: _polyline,
            returnPolyline: _returnPolyline,
            holder: _holder,
            maximumUnits: _maximumUnits,
          },
        },
      } = await API(state.jwt).get(`/quotations/${params.id}`);
      const _isRoundTrip = _itinerary.some((location) => location.isReturn);
      setItinerary(_itinerary);
      setNumberOfPassengers(_numberOfPassengers);
      setIsRoundTrip(_isRoundTrip);
      setPolyline(_polyline);
      setReturnPolyline(_returnPolyline);
      setMaximumUnits(_maximumUnits);
      const { data: result } = await API(state.jwt).get(
        `/quoter/${params.id}?cheapest=true`,
      );
      setResults(result);
      setHolder(_holder);
    } catch (error) {
      if (error?.response?.data?.error?.details?.finished) {
        setFinished(true);
      }
      status.updateErrorMessage(error);
    }
    setIsLoadingItinerary(false);
    setIsLoading(false);
  }, [params.id, state.jwt, status]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  useEffect(() => {
    if (map) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => !location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      map.fitBounds(bounds);
    }
  }, [map, itinerary]);

  useEffect(() => {
    if (isRoundTrip && returnMap) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      returnMap.fitBounds(bounds);
    }
  }, [isRoundTrip, returnMap, itinerary]);

  const onAdd = useCallback(
    (index) => {
      let _results = [...results];
      if (
        _results.reduce((total, result) => total + result.quantity, 0) >=
        maximumUnits
      ) {
        onOpenUnitsModal();
        return;
      }
      _results = _results.map((_result, _index) => ({
        ..._result,
        quantity: _result.quantity + (index === _index ? 1 : 0),
      }));
      setResults(_results);
    },
    [results, maximumUnits, onOpenUnitsModal],
  );

  const onRemove = useCallback(
    (index) => {
      let _results = [...results];
      _results = _results.map((_result, _index) => ({
        ..._result,
        quantity:
          _result.quantity > 0
            ? _result.quantity - (index === _index ? 1 : 0)
            : 0,
      }));
      setResults(_results);
    },
    [results],
  );

  const onRequestUnits = useCallback(async () => {
    try {
      setIsSaving(true);
      await API(state.jwt).put(`/quotations/${params.id}`, {
        data: { requestUnits: true },
      });
      status.updateSuccessMessage(
        "Solicitud enviada correctamente, Dentro de 24 a 48 horas recibirá respuesta de su solicitud",
      );
      onCloseUnitsModal();
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsSaving(false);
  }, [onCloseUnitsModal, params.id, state.jwt, status]);

  const totalNumberOfPassengers = useMemo(
    () =>
      results.reduce(
        (total, result) =>
          total + result.quantity * result.vehicleType.capacity,
        0,
      ),
    [results],
  );

  return (
    <VStack>
      <Helmet>
        <title>Selección de vehículo</title>
      </Helmet>
      <HStack space={10} zIndex={1} paddingY={8}>
        <Holder
          isLoading={isLoading}
          holder={holder}
          setHolder={setHolder}
          disabled
        />
      </HStack>
      <HStack space={3} alignItems="flex-start">
        <VStack space={5} maxWidth={300} width="full">
          <Heading color={colors.secondary} fontSize="2xl">
            Resumen de tu viaje
          </Heading>
          <Pressable
            backgroundColor={colors.secondary}
            paddingX={4}
            paddingY={2}
            borderRadius={5}
            onPress={onMapModalOpen}
            disabled={isLoading}>
            <HStack space={2}>
              <FiMap color={colors.white} size={20} />
              <Text color={colors.white}>Ver en mapa</Text>
            </HStack>
          </Pressable>
          <VStack backgroundColor={colors.white} padding={5}>
            {isLoadingItinerary ? (
              <Center height={300}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <Itinerary itinerary={itinerary} />
            )}
          </VStack>
        </VStack>
        <VStack flex={1} space={5}>
          <HStack space={4} alignItems="flex-start">
            <HStack space={4} flex={1}>
              <Heading color={colors.secondary} fontSize="2xl">
                Selecciona el vehículo
              </Heading>
              <HStack space={2} flex={1} alignItems="flex-start">
                <Image src={seat} alt="seat" width={5} height={5} />
                <Text color={colors.secondary}>{numberOfPassengers}</Text>
              </HStack>
            </HStack>
            <Link
              to={routes.agencyExperienceCreationId.replace(":id", params.id)}>
              <HStack
                space={3}
                paddingX={4}
                paddingY={2}
                borderColor={colors.primary}
                borderWidth={1}
                borderRadius="md">
                <FiEdit color={colors.primary} size={20} />
                <Text bold color={colors.primary}>
                  Editar
                </Text>
              </HStack>
            </Link>
          </HStack>
          <VStack backgroundColor={colors.white} padding={1}>
            {isLoading ? (
              <Center height={300}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Tipo de
                        <br />
                        unidad
                      </Text>
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Capacidad
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Amenidades
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Fotos
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        # de
                        <br />
                        unidades
                      </Text>
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Precio
                        <br />
                        unitario
                      </Text>
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Total
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Descuento
                        <br />%
                      </Text>
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Descuento
                        <br />$
                      </Text>
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      A pagar
                    </Cell>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={String(index)}>
                      <Cell bold fontSize="sm">
                        {result.vehicleType.name}
                      </Cell>
                      <Cell bold fontSize="sm">
                        {result.vehicleType.capacity}
                      </Cell>
                      <Cell>
                        <HStack space={1} justifyContent="center">
                          {result.vehicleType.amenities
                            .slice(0, 3)
                            .map((amenity) => (
                              <VStack
                                key={amenity.id}
                                alignItems="center"
                                space={1}>
                                <Image
                                  src={getFileUrl(amenity.image.url)}
                                  alt="vehicle"
                                  width={5}
                                  height={5}
                                />
                                <Text fontSize="sm" lineHeight="sm">
                                  {amenity.name}
                                </Text>
                              </VStack>
                            ))}
                        </HStack>
                      </Cell>
                      <Cell>
                        {Boolean(result.vehicleType.photos.length > 0) && (
                          <Pressable
                            onPress={() => {
                              setModalImages(result.vehicleType.photos);
                              setModalAmenities(result.vehicleType.amenities);
                            }}>
                            <Text underline color={colors.primary}>
                              Ver fotos
                            </Text>
                          </Pressable>
                        )}
                      </Cell>
                      <Cell>
                        <HStack space={2}>
                          <Pressable
                            onPress={() => onRemove(index)}
                            disabled={result.quantity === 0}>
                            <Image
                              src={removeCircle}
                              alt="remove-circle"
                              width={4}
                              height={4}
                            />
                          </Pressable>
                          <Text fontSize="sm">{result.quantity}</Text>
                          <Pressable onPress={() => onAdd(index)}>
                            <Image
                              src={addCircle}
                              alt="add-circle"
                              width={4}
                              height={4}
                            />
                          </Pressable>
                        </HStack>
                      </Cell>
                      <Cell bold fontSize="sm">
                        {formatToCurrency(
                          getOriginalPrice(result.price, result.coupon),
                        )}
                      </Cell>
                      <Cell bold fontSize="sm">
                        {formatToCurrency(
                          getOriginalPrice(result.price, result.coupon) *
                            result.quantity,
                        )}
                      </Cell>
                      <Cell bold fontSize="sm">
                        {formatCouponValue(result.coupon)}
                      </Cell>
                      <Cell bold fontSize="sm">
                        {formatToCurrency(
                          (getOriginalPrice(result.price, result.coupon) -
                            result.price) *
                            result.quantity,
                        )}
                      </Cell>
                      <Cell bold fontSize="sm">
                        {formatToCurrency(result.price * result.quantity)}
                      </Cell>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: colors.mail }}>
                    <Cell bold fontSize="sm" color={colors.white}>
                      Total
                    </Cell>
                    <Cell bold fontSize="sm" color={colors.white}>
                      {results
                        .reduce(
                          (total, result) =>
                            total +
                            result.vehicleType.capacity * result.quantity,
                          0,
                        )
                        .toString()}
                    </Cell>
                    <td />
                    <td />
                    <Cell bold fontSize="sm" color={colors.white}>
                      {results
                        .reduce((total, result) => total + result.quantity, 0)
                        .toString()}
                    </Cell>
                    <td />
                    <Cell bold fontSize="sm" color={colors.white}>
                      {formatToCurrency(
                        results.reduce(
                          (total, result) =>
                            total +
                            getOriginalPrice(result.price, result.coupon) *
                              result.quantity,
                          0,
                        ),
                      )}
                    </Cell>
                    <Cell bold fontSize="sm" color={colors.white}>
                      {Array.from(
                        new Set(
                          results.map((result) =>
                            formatCouponValue(result.coupon),
                          ),
                        ),
                      ).join(", ")}
                    </Cell>
                    <Cell bold fontSize="sm" color={colors.white}>
                      {formatToCurrency(
                        results.reduce(
                          (total, result) =>
                            total +
                            (getOriginalPrice(result.price, result.coupon) -
                              result.price) *
                              result.quantity,
                          0,
                        ),
                      )}
                    </Cell>
                    <Cell bold fontSize="sm" color={colors.white}>
                      {formatToCurrency(
                        results.reduce(
                          (total, result) =>
                            total + result.price * result.quantity,
                          0,
                        ),
                      )}
                    </Cell>
                  </tr>
                </tbody>
              </table>
            )}
          </VStack>
          <HStack justifyContent="flex-end" alignItems="flex-start" space={5}>
            <VStack alignItems="flex-end" space={1}>
              <Link
                to={routes.agencyExperienceCreationPurchaseSummary.replace(
                  ":id",
                  params.id,
                )}
                state={results}
                disabled={
                  totalNumberOfPassengers < numberOfPassengers || isLoading
                }>
                <Box
                  backgroundColor={colors.primary}
                  paddingY={3}
                  paddingX={100}
                  borderRadius="full"
                  width="fit-content">
                  <Text color={colors.white}>Continuar</Text>
                </Box>
              </Link>
              {totalNumberOfPassengers > 0 &&
                totalNumberOfPassengers < numberOfPassengers && (
                  <Text>
                    Capacidad de unidades seleccionadas no cubre el número de
                    pasajeros
                  </Text>
                )}
            </VStack>
            {state.isLoggedIn && (
              <Pressable onPress={onContactModalOpen}>
                <Stack
                  backgroundColor="#669999"
                  width={50}
                  height={50}
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="full">
                  <MdOutlineEmail size={35} color={colors.white} />
                </Stack>
              </Pressable>
            )}
          </HStack>
        </VStack>
      </HStack>
      <Modal
        maxWidth={900}
        isOpen={isMapModalOpen}
        onRequestClose={onMapModalClose}>
        <VStack padding={5}>
          {isRoundTrip && (
            <Text bold color={colors.primary} fontSize="2xl">
              Ida
            </Text>
          )}
          <Box height={400} width="full">
            <Map setRef={setMap}>
              {Boolean(polyline) && (
                <Polyline
                  path={decodePolyline(polyline)}
                  options={{
                    strokeColor: colors.primary,
                  }}
                />
              )}
              {itinerary
                .filter((location) => !location.isReturn)
                .map((location) => (
                  <Marker
                    key={location.id}
                    position={{
                      lat: location.latitude,
                      lng: location.longitude,
                    }}
                    icon={mapIcons[location.type]}
                    title={location.name}
                  />
                ))}
            </Map>
          </Box>
          {isRoundTrip && (
            <VStack marginTop={5}>
              <Text bold color={colors.primary} fontSize="2xl">
                Vuelta
              </Text>
              <Box height={400} width="full">
                <Map setRef={setReturnMap}>
                  {Boolean(returnPolyline) && (
                    <Polyline
                      path={decodePolyline(returnPolyline)}
                      options={{
                        strokeColor: colors.primary,
                      }}
                    />
                  )}
                  {itinerary
                    .filter((location) => location.isReturn)
                    .map((location) => (
                      <Marker
                        key={location.id}
                        position={{
                          lat: location.latitude,
                          lng: location.longitude,
                        }}
                        icon={mapIcons[location.type]}
                        title={location.name}
                      />
                    ))}
                </Map>
              </Box>
            </VStack>
          )}
        </VStack>
      </Modal>
      <QuotationFinishedModal isOpen={finished} />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={onContactModalClose}
        id={params.id}
      />
      <Modal
        maxWidth={900}
        isOpen={modalImages.length > 0}
        onRequestClose={() => setModalImages([])}>
        <VStack paddingX={5} paddingY={7}>
          <Heading color={colors.secondary} fontSize="2xl">
            Fotos del vehículo
          </Heading>
          <ImageGallery
            items={modalImages.map((image) => ({
              original: getFileUrl(image.url),
              thumbnail: getFileUrl(
                image?.formats?.thumbnail?.url || image.url,
              ),
              thumbnailHeight: 90,
              thumbnailWidth: 90,
            }))}
            showThumbnails
            showPlayButton={false}
          />
          <HStack space={3} justifyContent="center" marginTop={5}>
            {modalAmenities.map((amenity) => (
              <VStack key={amenity.id} alignItems="center" space={1}>
                <Image
                  src={getFileUrl(amenity.image.url)}
                  alt="vehicle"
                  width={5}
                  height={5}
                />
                <Text fontSize="sm" lineHeight="sm" textAlign="center">
                  {amenity.name}
                </Text>
              </VStack>
            ))}
          </HStack>
        </VStack>
      </Modal>
      <UnitsModal
        isOpen={isUnitsModalOpen}
        onRequestClose={isSaving ? null : onCloseUnitsModal}
        maximumUnits={maximumUnits}
        onRequestUnits={onRequestUnits}
        isSaving={isSaving}
      />
    </VStack>
  );
}

export default AgencyExperienceCreationTransportSelectionPage;
